import { legendSwatchDashoffset } from '../legend-swatch';
import {
  ChartTooltipColumnBlock,
  ChartTooltipGroup,
  ChartTooltipSwatchStyle,
} from './chart-tooltip-markup.types';

// Markup for the shared `.relative-chart-tooltip` styles: line swatches,
// model groups and the columns they are packed into (WDD, Renewables and
// Forecast Evolution graphs).

const GROUP_HEADER_ROWS = 1;
const DEFAULT_BORDER_WIDTH = 0.5;

// A 25x10 line like the legend's, with an optional outline under it and
// any extra markup (e.g. a run marker) drawn on top.
export function buildChartTooltipSwatchSvg(
  color: string,
  {
    lineWidth = 1,
    svgDasharray,
    strokeLinecap = 'butt',
    opacity = 1,
    borderColor,
    borderWidth = DEFAULT_BORDER_WIDTH,
  }: ChartTooltipSwatchStyle = {},
  extraSvg = ''
): string {
  const dashOffset = legendSwatchDashoffset(svgDasharray);
  const dashAttr =
    svgDasharray && svgDasharray !== 'none'
      ? ` stroke-dasharray="${svgDasharray}" stroke-dashoffset="${dashOffset}"`
      : '';
  const borderLine = borderColor
    ? `<line x1="0" y1="5" x2="25" y2="5" stroke="${borderColor}" stroke-width="${
        lineWidth + borderWidth * 2
      }"${dashAttr} stroke-linecap="${strokeLinecap}"/>`
    : '';

  return `<svg width="25" height="10" viewBox="0 0 25 10" xmlns="http://www.w3.org/2000/svg">${borderLine}<line x1="0" y1="5" x2="25" y2="5" stroke="${color}" stroke-width="${lineWidth}" stroke-linecap="${strokeLinecap}"${dashAttr} opacity="${opacity}"/>${extraSvg}</svg>`;
}

export function buildChartTooltipRow(
  swatchSvg: string,
  label: string,
  value: string
): string {
  return `<div class="tooltip-row"><span class="legend-icon">${swatchSvg}</span><span class="series-name">${label}</span><span class="series-value">${value}</span></div>`;
}

/**
 * Stacks the groups one under another, column by column. A group fills what
 * is left of the current column (one row goes to its header) and spills the
 * rest into the next one, repeating its header there; no column goes over
 * maxRowsPerColumn rows.
 */
export function packChartTooltipGroups(
  groups: ChartTooltipGroup[],
  maxRowsPerColumn: number
): ChartTooltipColumnBlock[][] {
  const columns: ChartTooltipColumnBlock[][] = [[]];
  const heights: number[] = [0];

  groups.forEach(({ displayName, rowHtmls }) => {
    const remaining = [...rowHtmls];

    while (remaining.length) {
      const last = columns.length - 1;
      const available = maxRowsPerColumn - heights[last] - GROUP_HEADER_ROWS;

      if (available <= 0) {
        columns.push([]);
        heights.push(0);
        continue;
      }

      const slice = remaining.splice(0, available);
      const height = slice.length + GROUP_HEADER_ROWS;
      columns[last].push({ displayName, rowsHtml: slice.join(''), height });
      heights[last] += height;
    }
  });

  return columns.filter((column) => column.length > 0);
}

// Rows without a model header (normals, observations) go under the last
// column, or make the only column when there is none.
export function appendChartTooltipStaticBlock(
  columns: ChartTooltipColumnBlock[][],
  rowHtmls: string[]
): ChartTooltipColumnBlock[][] {
  if (!rowHtmls.length) return columns;

  const block: ChartTooltipColumnBlock = {
    displayName: '',
    rowsHtml: rowHtmls.join(''),
    height: rowHtmls.length + GROUP_HEADER_ROWS,
  };

  return columns.length
    ? [...columns.slice(0, -1), [...columns[columns.length - 1], block]]
    : [[block]];
}

export function renderChartTooltipColumns(
  columns: ChartTooltipColumnBlock[][]
): string {
  return columns
    .map(
      (column) =>
        `<div class="tooltip-column">${column
          .map(
            ({ displayName, rowsHtml }) =>
              `<div class="model-group"><div class="model-group-name">${displayName}</div>${rowsHtml}</div>`
          )
          .join('')}</div>`
    )
    .join('');
}
